import React, { useState, useRef, useEffect } from "react";
import AudioEditorLoaderSidebar from "./AudioEditorLoaderSidebar";
import AudioEditorLoaderPreview from "./AudioEditorLoaderPreview";
import styles from "./AudioEditorLoader.module.css";

const STACK_BREAKPOINT = 1024;

const AudioEditorLoader = () => {
    const [height, setHeight] = useState(200);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isStacked, setIsStacked] = useState(
        () => typeof window !== "undefined" && window.matchMedia(`(max-width: ${STACK_BREAKPOINT}px)`).matches,
    );

    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${STACK_BREAKPOINT}px)`);
        const onChange = () => setIsStacked(mq.matches);
        onChange();
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    useEffect(() => {
        if (isStacked) {
            setIsDragging(false);
            return;
        }

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging || !containerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const containerTop = containerRect.top;
            const newHeight = Math.min(500, Math.max(200, e.clientY - containerTop));
            setHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, isStacked]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isStacked) return;
        e.preventDefault();
        setIsDragging(true);
    };

    return (
        <div
            className={`${styles.loader}${isStacked ? ` ${styles.loaderStacked}` : ""}`}
            style={isStacked ? undefined : { height: `${height}px` }}
            ref={containerRef}
        >
            <AudioEditorLoaderSidebar />
            <div className={styles.preview}>
                <AudioEditorLoaderPreview />
            </div>
            {!isStacked && (
                <div
                    className={`${styles.resizeHandle} ${isDragging ? styles.resizeHandleActive : ""}`}
                    ref={resizeHandleRef}
                    onMouseDown={handleMouseDown}
                />
            )}
        </div>
    );
};

export default AudioEditorLoader;
